import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Video, Clock, ArrowRight, BookOpen } from 'lucide-react';

export function SessionBlockingOverlay() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);

  const fetchAlerts = useCallback(async () => {
    if (!user) return;
    const role = user.active_role || user.role;
    if (role !== 'instructor' && role !== 'parent') return;
    try {
      const res = await api.get('/sessions/active-alerts');
      setAlerts(res.data.alerts || []);
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 60000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const dismissVideoAlert = async (sessionId) => {
    try {
      await api.put(`/sessions/${sessionId}/dismiss-video-alert`);
      setAlerts(prev => prev.filter(a => !(a.type === 'upload_videos' && a.session_id === sessionId)));
    } catch { /* silent */ }
  };

  const dismissStoryboardAlert = async (sessionId) => {
    try {
      await api.put(`/sessions/${sessionId}/dismiss-storyboard-alert`);
      setAlerts(prev => prev.filter(a => !(a.type === 'storyboard_needed' && a.session_id === sessionId)));
    } catch { /* silent */ }
  };

  if (alerts.length === 0) return null;

  // Priority: end_session > upload_videos > storyboard_needed
  const endAlerts = alerts.filter(a => a.type === 'end_session');
  const videoAlerts = alerts.filter(a => a.type === 'upload_videos');
  const storyboardAlerts = alerts.filter(a => a.type === 'storyboard_needed');
  const primary = endAlerts[0] || videoAlerts[0] || storyboardAlerts[0];

  if (!primary) return null;

  const isEnd = primary.type === 'end_session';
  const isVideo = primary.type === 'upload_videos';
  const isStoryboard = primary.type === 'storyboard_needed';

  const getIcon = () => {
    if (isEnd) return <Clock className="w-8 h-8 text-red-600" />;
    if (isVideo) return <Video className="w-8 h-8 text-amber-600" />;
    return <BookOpen className="w-8 h-8 text-[#0E7490]" />;
  };

  const getIconBg = () => {
    if (isEnd) return 'bg-red-100';
    if (isVideo) return 'bg-amber-100';
    return 'bg-[#ECFEFF]';
  };

  const getTitle = () => {
    if (isEnd) return 'Session Still Running';
    if (isVideo) return 'Videos Need Uploading';
    return "It's Storyboard Time!";
  };

  const getDescription = () => {
    if (isEnd) return (
      <>
        <strong>Session #{primary.session_number}</strong> with <strong>{primary.child_name}</strong> has
        been running for <strong>{primary.elapsed_hours} hours</strong>.
      </>
    );
    if (isVideo) return (
      <>
        <strong>Session #{primary.session_number}</strong> with <strong>{primary.child_name}</strong> ended
        <strong> {primary.elapsed_hours} hours ago</strong>. You've uploaded{' '}
        <strong>{primary.videos_uploaded}</strong> of <strong>{primary.skills_completed}</strong> skill videos.
      </>
    );
    return (
      <>
        <strong>{primary.child_name}</strong>'s Session #{primary.session_number} has{' '}
        <strong>{primary.videos_available}</strong> video{primary.videos_available !== 1 ? 's' : ''} ready!
        Build the storyboard together to celebrate their progress.
        {primary.storyboard_entries > 0 && (
          <> You've started with <strong>{primary.storyboard_entries}</strong> entr{primary.storyboard_entries !== 1 ? 'ies' : 'y'}.</>
        )}
      </>
    );
  };

  const getButtonLabel = () => {
    if (isEnd) return 'End Session Now';
    if (isVideo) return 'Upload Videos';
    return 'Build Storyboard';
  };

  const getButtonColor = () => {
    if (isEnd) return 'bg-red-600 hover:bg-red-700';
    if (isVideo) return 'bg-amber-600 hover:bg-amber-700';
    return 'bg-[#0E7490] hover:bg-[#0C5D73]';
  };

  const getDestination = () => {
    if (isEnd) return `/dashboard/session/${primary.session_id}`;
    return `/dashboard/storyboard/${primary.child_id}`;
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="session-blocking-overlay">
      <div className="absolute inset-0 bg-[#1E1B4B]/80 backdrop-blur-md" />
      <div className="relative bg-white rounded-3xl shadow-2xl max-w-md w-[90vw] p-8 text-center animate-fadeIn">
        <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-5 ${getIconBg()}`}>
          {getIcon()}
        </div>

        <h2 className="text-xl font-extrabold text-[#1E1B4B] mb-2" style={{ fontFamily: 'Nunito' }}>
          {getTitle()}
        </h2>

        <p className="text-slate-600 text-sm leading-relaxed mb-2">
          {getDescription()}
        </p>

        {isEnd && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-5 text-left">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
              <p className="text-xs text-red-700 leading-relaxed">
                Complete the assessment <strong>today</strong>. If you delay, the facility manager may
                record the admission on the wrong day.
              </p>
            </div>
          </div>
        )}

        {isVideo && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5 text-left">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-700 leading-relaxed">
                Parents need the videos to build the storyboard. Please upload
                the remaining {primary.skills_completed - primary.videos_uploaded} video{primary.skills_completed - primary.videos_uploaded !== 1 ? 's' : ''}.
              </p>
            </div>
          </div>
        )}

        {isStoryboard && (
          <div className="bg-[#ECFEFF] border border-[#22D3EE] rounded-xl p-3 mb-5 text-left">
            <div className="flex items-start gap-2">
              <BookOpen className="w-4 h-4 text-[#0E7490] mt-0.5 shrink-0" />
              <p className="text-xs text-[#0E7490] leading-relaxed">
                The storyboard helps your child remember and celebrate what they learned.
                Sit together and talk through each skill!
              </p>
            </div>
          </div>
        )}

        <Button
          onClick={() => {
            setAlerts([]);
            navigate(getDestination());
          }}
          className={`w-full rounded-full font-bold h-12 gap-2 text-white ${getButtonColor()}`}
          data-testid="blocking-overlay-action-btn"
        >
          {getButtonLabel()}
          <ArrowRight className="w-4 h-4" />
        </Button>

        {(isVideo || isStoryboard) && (
          <button
            onClick={() => isVideo ? dismissVideoAlert(primary.session_id) : dismissStoryboardAlert(primary.session_id)}
            className="mt-3 text-xs text-slate-400 hover:text-slate-600 transition-colors"
            data-testid="dismiss-alert-btn"
          >
            {isVideo ? "I've uploaded all available videos — dismiss" : "I'll do this later — dismiss"}
          </button>
        )}
      </div>
    </div>
  );
}
